import React from "react";
import SEO from "../components/SEO";
import flowImg from "../images/how-it-works-flow.svg";

export default function HowItWorks() {
  const steps = [
    {
      k: "01",
      h: "Connect your tools",
      p: "Link chat, email, tasks, code, calendar, and CRM in minutes: Slack, Teams, Gmail, Outlook, Asana, Jira, Linear, ClickUp, Notion, monday.com, Planner, GitHub, Google Calendar, HubSpot, and Pipedrive. Read-only wherever it can be.",
    },
    {
      k: "02",
      h: "Detect & score",
      p: "BossDrop reads issues, owners, timestamps, and risk language across every tool to build one live picture of delivery.",
    },
    {
      k: "03",
      h: "Decide (plan + critic)",
      p: "The AI drafts each decision (owner, deadline, measurable target) from what’s happening now and what it has learned about how your team runs, then a second pass critiques and grounds it, ranked by impact.",
    },
    {
      k: "04",
      h: "Act, with approval where you want it",
      p: "BossDrop nudges owners, escalates blockers, and schedules only the meetings that matter. Any automation can wait for your one-click approval instead, whenever you want it to.",
    },
    {
      k: "05",
      h: "Learn and close the loop",
      p: "Every action is tracked and written to the audit log. BossDrop sees how each one turned out, rewrites its memory of how your team runs every day, and changes what it does tomorrow.",
    },
  ];
  return (
    <main className="page">
      {" "}
      <SEO
        title="BossDrop | How it Works"
        description="See how BossDrop works: connect tools, the AI surfaces risks, drafts decisions, schedules huddles, nudges owners, and learns how your team runs every day."
        canonical="https://bossdrop.ai/how-it-works"
        url="https://bossdrop.ai/how-it-works"
        /* image="https://bossdrop.ai/og/pricing-1200x630.jpg" */
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: "BossDrop",
          applicationCategory: "BusinessApplication",
          operatingSystem: "Web",
          url: "https://bossdrop.ai/",
          publisher: {
            "@type": "Organization",
            name: "BossDrop",
            url: "https://bossdrop.ai/",
          },
        }}
        breadcrumbsJsonLd={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: "https://bossdrop.ai/",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "How it Works",
              item: "https://bossdrop.ai/how-it-works",
            },
          ],
        }}
      />
      <section className="hero">
        <h1 className="h1">How it Works</h1>
        <p className="sub">
          Five steps from signals to decisions, with no dashboards to maintain.
        </p>
      </section>
      <figure
        style={{
          margin: "8px auto 28px",
          maxWidth: 1000,
          padding: "0 4px",
        }}
      >
        <div className="figWrap">
          <img
            src={flowImg}
            alt="BossDrop's governed pipeline: connect tools, detect risks and blockers, decide with a plan and critic, get approval on high-impact actions, execute, and close the loop with a full audit trail"
            decoding="async"
            style={{
              width: "100%",
              height: "auto",
              display: "block",
            }}
          />
        </div>
      </figure>
      <section className="flow">
        {steps.map((s, i) => (
          <div className="step" key={i}>
            <div className="badge">{s.k}</div>
            <div className="stepBody">
              <h3>{s.h}</h3>
              <p>{s.p}</p>
            </div>
          </div>
        ))}
      </section>
      <section className="panel panelDark">
        <h2 className="h2">How it remembers your team</h2>
        <ul className="bullets">
          <li>
            <strong>It watches real work.</strong> Who it asked and how quickly
            they answered, when work finished, moved, or changed hands, and which
            escalations got things moving. For timing, it keeps people, times,
            and task references, not the text of your messages.
          </li>
          <li>
            <strong>It rewrites one document every day.</strong> Owners, cadence,
            handoffs, policies, and thresholds, which your team can read on the
            Memory page. Lines about one person are shown only to admins and that
            person.
          </li>
          <li>
            <strong>It changes what happens tomorrow.</strong> That document
            shapes who BossDrop asks, when it checks in, and how long it waits
            before going to a manager.
          </li>
          <li>
            <strong>It stays yours.</strong> Admins can correct any line in their
            own words, which outranks what BossDrop measured, and export all of it
            at any time. It never ranks people.
          </li>
        </ul>
      </section>
      <section className="panel panelDark">
        <h2 className="h2">Why it’s different</h2>
        <ul className="bullets">
          <li>
            No generic “fix/resolve”: every action is owner + deadline + metric.
          </li>
          <li>Exec brevity by default: weekly summaries under 140 words.</li>
          <li>BossDrop actually does the work, not just suggest.</li>
        </ul>
      </section>
      <section className="ctaBand">
        <div className="ctaInner">
          <h2 className="h2">Ready to start?</h2>
          <p className="sub">Connect your project management tools in minutes.</p>
          <a className="btn btnPrimary" href="/pricing">
            Get started
          </a>
        </div>
      </section>
    </main>
  );
}
